import type { HTMLAttributes } from "react";
import type { IconName } from "../icons";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

export type BadgeTone = "neutral" | "brand" | "success" | "warning" | "danger";

const TONE_CLASS: Record<BadgeTone, string> = {
  neutral: "badge-neutral",
  brand: "badge-brand",
  success: "badge-success",
  warning: "badge-warning",
  danger: "badge-danger",
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  dot?: boolean;
}

export function Badge({ tone = "neutral", dot = false, className, children, ...rest }: BadgeProps) {
  return (
    <span className={cn("badge", TONE_CLASS[tone], className)} {...rest}>
      {dot ? <span className="dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

export type StatusKind = "success" | "warning" | "danger" | "info" | "neutral";

const STATUS: Record<StatusKind, { tone: BadgeTone; icon: IconName }> = {
  success: { tone: "success", icon: "check-circle" },
  warning: { tone: "warning", icon: "alert-triangle" },
  danger: { tone: "danger", icon: "x-circle" },
  info: { tone: "brand", icon: "info" },
  neutral: { tone: "neutral", icon: "circle" },
};

export interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  status: StatusKind;
  /** Visible text — status is never conveyed by colour alone. */
  label: string;
  icon?: IconName;
}

/** Status = icon + text + colour (CLAUDE.md rule 10). */
export function StatusBadge({ status, label, icon, className, ...rest }: StatusBadgeProps) {
  const spec = STATUS[status];
  return (
    <span className={cn("badge", TONE_CLASS[spec.tone], className)} data-status={status} {...rest}>
      <Icon name={icon ?? spec.icon} className="size-3" />
      {label}
    </span>
  );
}
